/**
 * Skeleton Component
 *
 * Base skeleton with shimmer animation for loading states.
 * Meta standard: Skeleton mirrors actual content layout.
 *
 * Design Principles:
 * - Match content structure (user knows what to expect)
 * - Smooth shimmer animation (indicates progress)
 * - Accessible (aria labels for screen readers)
 * - Performant (CSS animations, GPU accelerated)
 *
 * @module Skeleton
 */

import './Skeleton.css'

/**
 * Skeleton Props
 */
export interface SkeletonProps {
  /** Visual variant */
  variant?: 'text' | 'rectangular' | 'circular'

  /** Width (px or string with unit) */
  width?: string | number

  /** Height (px or string with unit) */
  height?: string | number

  /** Additional CSS class */
  className?: string

  /** Animation speed (ms) - defaults to 1500ms */
  animation?: number | false
}

/**
 * Skeleton Component
 *
 * A placeholder component that shows where content will appear.
 * Uses shimmer animation to indicate loading state.
 *
 * Variants:
 * - text: For text lines (12px height, small border radius)
 * - rectangular: For blocks, cards, images (4px border radius)
 * - circular: For avatars, icons (50% border radius)
 *
 * @example
 * ```tsx
 * // Text line
 * <Skeleton variant="text" width="80%" />
 *
 * // Card/block
 * <Skeleton variant="rectangular" width={300} height={200} />
 *
 * // Avatar
 * <Skeleton variant="circular" width={40} height={40} />
 * ```
 */
export function Skeleton({
  variant = 'text',
  width,
  height,
  className = '',
  animation = 1500,
}: SkeletonProps) {
  // Convert number to px
  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  }

  // Disable animation if requested
  if (animation === false) {
    style.animation = 'none'
  } else if (animation !== 1500) {
    style.animationDuration = `${animation}ms`
  }

  return (
    <div
      className={`skeleton skeleton--${variant} ${className}`}
      style={style}
      aria-busy="true"
      aria-live="polite"
      role="status"
      aria-label="Loading content"
    />
  )
}

/**
 * SkeletonText - Convenience component for text lines
 *
 * Pre-configured for text content with sensible defaults.
 *
 * @example
 * ```tsx
 * <SkeletonText lines={3} />
 * ```
 */
export interface SkeletonTextProps {
  /** Number of text lines to show */
  lines?: number

  /** Width of each line (defaults to decreasing widths) */
  widths?: string[]

  /** Spacing between lines (px) */
  spacing?: number
}

export function SkeletonText({ lines = 1, widths, spacing = 8 }: SkeletonTextProps) {
  // Default widths: First line 100%, subsequent lines decrease
  const defaultWidths = Array.from({ length: lines }, (_, i) => {
    if (i === 0) return '100%'
    if (i === lines - 1) return `${60 + ((i * 7) % 20)}%` // Last line shorter (stable per index)
    return `${85 + ((i * 3) % 10)}%`
  })

  const lineWidths = widths || defaultWidths

  return (
    <div className="skeleton-text">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={`${lineWidths[i]}-${i}`}
          variant="text"
          width={lineWidths[i]}
          className="skeleton-text__line"
          style={{ marginBottom: i < lines - 1 ? `${spacing}px` : 0 }}
        />
      ))}
    </div>
  )
}

/**
 * Accessibility Notes:
 *
 * - aria-busy="true": Tells screen readers content is loading
 * - aria-live="polite": Will announce when content finishes loading
 * - role="status": Identifies as status indicator
 * - aria-label: Describes what's happening
 *
 * Screen reader experience:
 * 1. User navigates to skeleton
 * 2. Screen reader: "Loading content, busy"
 * 3. Content loads
 * 4. Screen reader: "Content loaded" (via aria-live)
 */

/**
 * Performance Notes:
 *
 * 1. CSS Animation (not JS):
 *    - Runs on GPU (compositor thread)
 *    - Doesn't block main thread
 *    - Smooth 60fps animation
 *    - No re-renders
 *
 * 2. Background gradient animation:
 *    - Background-position change (GPU accelerated)
 *    - Will-change hint for optimization
 *    - Transform translateZ(0) for layer promotion
 *
 * 3. Memory footprint:
 *    - Simple div with CSS (minimal memory)
 *    - No images or complex markup
 *    - Hundreds can render without lag
 */

/**
 * Design System Integration:
 *
 * Colors use CSS custom properties:
 * - --surface-alt: Base skeleton color
 * - --surface: Shimmer highlight color
 *
 * This ensures skeletons match theme automatically:
 * - Light mode: Light gray skeleton
 * - Dark mode: Dark gray skeleton
 * - High contrast: Adjusted automatically
 */
